// Web Push client for streak reminders.
import { lastSolvedDay, currentStreak } from './stats';

const ENABLED_KEY = 'dari-push-enabled';
const ENDPOINT_KEY = 'dari-push-endpoint';
export const REMINDER_HOUR = 19; // 7pm, in the user's local timezone

export function pushSupported() {
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export function isPushEnabled() {
  return localStorage.getItem(ENABLED_KEY) === '1';
}

export function notificationPermission() {
  return 'Notification' in window ? Notification.permission : 'denied';
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

async function getVapidKey() {
  const res = await fetch('/api/push/vapid');
  const { publicKey } = await res.json();
  return publicKey;
}

function statePayload() {
  return {
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    reminderHour: REMINDER_HOUR,
    lastSolvedDay: lastSolvedDay(),
    streak: currentStreak(),
  };
}

// Register SW, request permission, subscribe, and report state to the server.
// Returns true on success. Throws/returns false if permission denied or unsupported.
export async function enablePushReminders() {
  if (!pushSupported()) throw new Error('Push not supported on this browser.');

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return false;

  const reg = await navigator.serviceWorker.register('/sw.js');
  await navigator.serviceWorker.ready;

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    const vapid = await getVapidKey();
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapid),
    });
  }

  await fetch('/api/push/subscribe', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ subscription: sub, ...statePayload() }),
  });

  localStorage.setItem(ENABLED_KEY, '1');
  localStorage.setItem(ENDPOINT_KEY, sub.endpoint);
  return true;
}

export async function disablePushReminders() {
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = reg && (await reg.pushManager.getSubscription());
    if (sub) {
      await fetch('/api/push/unsubscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ endpoint: sub.endpoint }),
      });
      await sub.unsubscribe();
    }
  } catch (e) {
    // best effort
  }
  localStorage.removeItem(ENABLED_KEY);
  localStorage.removeItem(ENDPOINT_KEY);
}

// Called after a solve to keep the server's streak state fresh. No-op if disabled.
export function reportSolve() {
  if (!isPushEnabled()) return;
  const endpoint = localStorage.getItem(ENDPOINT_KEY);
  if (!endpoint) return;
  fetch('/api/push/state', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      endpoint,
      lastSolvedDay: lastSolvedDay(),
      streak: currentStreak(),
    }),
  }).catch(() => {});
}
